"use client";

import type { AnalyzeData } from "@/types/analyze";
import { Info, ExternalLink, Lock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getAuditDescription } from "@/lib/psi-audit-descriptions";

interface AccessibilityDetailsProps {
  device: "mobile" | "desktop";
  pageSpeed?: AnalyzeData["pageSpeed"];
  planType?: AnalyzeData["planType"];
}

interface AccessibilityCheck {
  id: string;
  title: string;
  points: {
    achieved: number;
    max: number;
  };
  normalized: {
    achieved: number;
    max: number;
  };
  locked?: boolean;
}

interface AccessibilitySummary {
  categories?: {
    accessibility?: {
      score: number | null;
      points: {
        achieved: number;
        max: number;
      };
      checks?: AccessibilityCheck[];
    };
  };
}

export function AccessibilityDetails({
  device,
  pageSpeed,
  planType,
}: AccessibilityDetailsProps) {
  const deviceData =
    device === "mobile" ? pageSpeed?.mobile : pageSpeed?.desktop;
  const accessibilityScore = Math.round(
    deviceData?.scores?.accessibilityScore ?? 0
  );
  const isPaidPlan = planType === "paid";

  const getAccessibilityCheckStatusColor = (
    normalizedAchieved: number,
    normalizedMax: number
  ): string => {
    if (!normalizedMax || normalizedMax <= 0) return "bg-muted-foreground/40";
    const successRatio = normalizedAchieved / normalizedMax;
    if (successRatio >= 0.8) return "bg-status-green";
    if (successRatio >= 0.6) return "bg-status-orange";
    return "bg-status-red";
  };

  // Parse summary data if available
  const accessibilitySummary: AccessibilitySummary | null = deviceData?.summary
    ? (deviceData.summary as AccessibilitySummary)
    : null;

  const accessibilityChecksData =
    accessibilitySummary?.categories?.accessibility?.checks ?? [];

  // Filter out checks with 0/0 (not applicable) and sort by max points (highest first)
  const sortedAccessibilityChecks = [...accessibilityChecksData]
    .filter((check) => {
      if ((check.locked ?? false) === true) {
        return true;
      }
      return !(check.normalized.achieved === 0 && check.normalized.max === 0);
    })
    .sort((a, b) => b.points.max - a.points.max);

  return (
    <div className="rounded-md border border-border/50 p-3">
      <div className="mb-6 flex items-center justify-between">
        <div className="text-base font-bold text-foreground">Accessibility</div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium">
          {accessibilityScore}/100
        </span>
      </div>
      {!deviceData ? (
        <div className="text-xs text-muted-foreground">
          No {device} PageSpeed details
        </div>
      ) : sortedAccessibilityChecks.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          Accessibility checks are not available in the current data format.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {sortedAccessibilityChecks.map((accessibilityCheck) => {
            const isLockedCheck =
              !isPaidPlan &&
              Boolean(
                (accessibilityCheck as { locked?: boolean }).locked === true
              );
            const statusColor = isLockedCheck
              ? "bg-muted-foreground/40"
              : getAccessibilityCheckStatusColor(
                  accessibilityCheck.normalized.achieved,
                  accessibilityCheck.normalized.max
                );
            const auditDetails = getAuditDescription(accessibilityCheck.id);

            return (
              <div key={accessibilityCheck.id} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${statusColor}`}
                    />
                    <div className="text-sm font-medium text-foreground">
                      {accessibilityCheck.title}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                      </PopoverTrigger>
                      <PopoverContent className="w-80 max-w-[90vw]">
                        <div className="space-y-2">
                          <p className="text-sm font-medium">{auditDetails.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {auditDetails.description}
                          </p>
                          {auditDetails.link && (
                            <a
                              href={auditDetails.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-foreground hover:text-foreground/80 underline inline-flex items-center gap-1"
                            >
                              Learn more <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {isLockedCheck ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                      <Lock className="h-3 w-3" />
                      Locked
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                      {accessibilityCheck.normalized.achieved}/
                      {accessibilityCheck.normalized.max}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
